import SectionHeading from "./section-heading";
import React from "react";
import {Link, useTranslation} from "gatsby-plugin-react-i18next";
import {Button} from "../../components/ui/button";
import {
    BookOpen,
    GlobeHemisphereWest,
    Lightning,
    Plugs,
} from "@phosphor-icons/react";

const EmailMockup = () => {
    const {t} = useTranslation();
    return (
    <div className="w-full max-w-[420px] mx-auto">
        <div className="relative">
            <div className="relative bg-[var(--cl-surface)] rounded-[4px] border border-[var(--cl-rule)] overflow-hidden">

                <div className="bg-[var(--cl-ink)] px-5 py-3.5 flex items-center gap-2.5">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="4" width="20" height="16" rx="2"/>
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                    </svg>
                    <span className="text-white font-semibold tracking-tight">Email Copilot</span>
                    <div className="ml-auto flex gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-white/30"/>
                        <div className="w-2 h-2 rounded-full bg-white/30"/>
                        <div className="w-2 h-2 rounded-full bg-white/30"/>
                    </div>
                </div>

                <div className="px-5 py-3.5 border-b border-[var(--cl-rule)]">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="font-semibold text-[var(--cl-ink)] text-base truncate">{t("Re: Product inquiry")}</div>
                            <div className="text-xs text-[var(--cl-muted)] mt-0.5">customer@example.com</div>
                        </div>
                        <button className="shrink-0 flex items-center gap-1.5 text-xs bg-[var(--cl-surface-2)] text-[var(--cl-ink)] px-3 py-1.5 rounded-full border border-[var(--cl-rule)] font-medium">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="m5 8 6 6"/>
                                <path d="m4 14 6-6 2-3"/>
                                <path d="M2 5h12"/>
                                <path d="M7 2h1"/>
                                <path d="m22 22-5-10-5 10"/>
                                <path d="M14 18h6"/>
                            </svg>
                            {t("Translate")}
                        </button>
                    </div>
                </div>

                <div className="px-5 py-3.5 bg-[var(--cl-surface-2)] text-[var(--cl-muted)] text-[13px] leading-relaxed border-b border-[var(--cl-rule)]">
                    {t("Hi, I'm interested in your premium plan. Could you tell me more about the features included and whether there's a trial period available?")}
                </div>

                <div className="px-5 py-4 border-b border-[var(--cl-rule)]">
                    <div className="flex items-center gap-2 mb-2.5">
                        <div className="w-2 h-2 rounded-full bg-[var(--cl-accent)]"/>
                        <span className="cl-eyebrow">{t("AI Suggested Response")}</span>
                    </div>
                    <div className="bg-[var(--cl-surface-2)] rounded-[4px] p-3.5 text-[13px] text-[var(--cl-ink-2)] leading-relaxed border border-[var(--cl-rule)]">
                        {t("Thank you for your interest! Our Premium plan includes 50,000 message credits, 25 chatbots, custom domain support, and agency features. Every paid plan starts with a free trial, so you can explore all features before...")}
                    </div>
                </div>

                <div className="px-5 py-3.5 flex gap-2.5">
                    <button className="flex-1 flex items-center justify-center gap-2 bg-[var(--cl-ink)] text-white text-[13px] font-semibold py-2.5 rounded-[4px]">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                        </svg>
                        {t("Suggest Response")}
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-2 bg-[var(--cl-surface)] text-[var(--cl-ink-2)] text-[13px] font-medium py-2.5 rounded-[4px] border border-[var(--cl-rule)]">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 5v14"/>
                            <path d="m19 12-7 7-7-7"/>
                        </svg>
                        {t("Insert into reply")}
                    </button>
                </div>
            </div>
        </div>
    </div>
    );
};

const BulletItem = ({icon: Icon, text}) => (
    <li className="flex items-start gap-3">
        <div className="shrink-0 mt-1 w-8 h-8 rounded-[4px] bg-[var(--cl-surface-2)] border border-[var(--cl-rule)] flex items-center justify-center">
            <Icon size={18} weight="regular" className="text-[var(--cl-ink)]"/>
        </div>
        <span className="text-base sm:text-lg text-[var(--cl-ink-2)] leading-relaxed">{text}</span>
    </li>
);

const EmailCopilotSection = () => {
    const {t} = useTranslation();
    return (
        <div className="w-full">
            <section className="max-w-screen-2xl mx-auto p-8 sm:p-10 lg:p-14 bg-[var(--cl-surface)] rounded-[4px]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

                    <div className="flex justify-center lg:justify-start order-2 lg:order-1">
                        <EmailMockup/>
                    </div>

                    <div className="flex flex-col gap-6 order-1 lg:order-2">
                        <SectionHeading
                            eyebrow={t("Same knowledge, another channel")}
                            title={t("AI Email Copilot")}
                        />

                        <p className="text-base sm:text-lg text-[var(--cl-ink-2)] leading-relaxed max-w-lg">
                            {t("Email Copilot reads incoming messages and drafts replies grounded in the same knowledge your agent uses in chat. Your team reviews and sends - nothing goes out on its own.")}
                        </p>

                        <ul className="flex flex-col gap-4 mt-2">
                            <BulletItem
                                icon={BookOpen}
                                text={t("Uses your chatbot's trained knowledge for accurate, consistent replies")}
                            />
                            <BulletItem
                                icon={GlobeHemisphereWest}
                                text={t("Translates incoming and outgoing emails across 80+ languages")}
                            />
                            <BulletItem
                                icon={Lightning}
                                text={t("AI drafts professional responses in seconds - you review and send")}
                            />
                            <BulletItem
                                icon={Plugs}
                                text={t("Works with any email provider via IMAP or POP3")}
                            />
                        </ul>

                        <div className="mt-4">
                            <Button asChild variant="regular-outline-black" className="px-5 flex items-center gap-3 text-base whitespace-nowrap w-fit">
                                <Link to="/help/email-copilot/" className="flex items-center gap-3">
                                    {t("Learn more about Email Copilot")}
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default EmailCopilotSection;
